import { useState, useEffect, useRef } from "react";
import { useRevealSecret } from "src/queries/hooks/collections";
import useAppStore from "src/stores/appStore";
import { hasPermissionSelector } from "src/stores/selectors/appSelectors";
import { USER_PERMISSIONS } from "@hyro/dashboard-permissions";

const useSecret = ({ value, isReadOnly, onChange, addSnackbarMessage, secretPath, key }) => {
  const [isValueLocked, setIsValueLocked] = useState(!value);
  // This is used to prevent the user from editing the hidden value, but still allow to replace it with a new value
  const [isEditingHiddenValue, setIsEditingHiddenValue] = useState(false);
  const [localValue, setLocalValue] = useState(value);
  const textFieldRef = useRef(null);
  const { mutateAsync: revealSecret, isPending } = useRevealSecret(secretPath, key);
  const hasPermission = useAppStore(hasPermissionSelector);

  useEffect(() => {
    if (isReadOnly) {
      setIsValueLocked(false);
    } else {
      setIsValueLocked(!value);
    }
  }, [isReadOnly]);

  const handleTextFieldBlur = () => {
    if (localValue) {
      setIsValueLocked(false);
      // Set explicitly to avoid showing the secret length by showing more/less stars
      setLocalValue("********");
    }
  };

  const localOnChange = (value) => {
    if (isValueLocked) {
      setLocalValue(value);
      onChange(value);
    }
  };

  const onButtonClick = async () => {
    if (!isValueLocked) {
      textFieldRef.current.focus();

      if (hasPermission(USER_PERMISSIONS.ABILITIES.REVEAL_INTEGRATION_SECRETS_ABILITY)) {
        // If we already fetched the secret, we can show it
        if (value && !value.startsWith("**")) {
          setLocalValue(value);
          setIsValueLocked(prev => !prev);
          return;
        }
        const secret = await revealSecret();

        if (!secret?.error) {
          setLocalValue(secret);
          setIsValueLocked(prev => !prev);
        } else {
          addSnackbarMessage("Error fetching secret, please try again later");
        }
      } else {
        setIsValueLocked(prev => !prev);
        textFieldRef.current.select();
        setIsEditingHiddenValue(true);
      }
    } else {
      setIsValueLocked(prev => !prev);
    }
  };

  const handleKeyDown = () => {
    if (isEditingHiddenValue) {
      textFieldRef.current.value = "";
      setLocalValue("");
      setIsEditingHiddenValue(false);
    }
  };

  return {
    isValueLocked,
    localValue,
    isLoading: isPending,
    textFieldRef,
    handleTextFieldBlur,
    localOnChange,
    onButtonClick,
    handleKeyDown
  };
};

export default useSecret;
